<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="$emit('close')">
    <div class="log-detail-modal">
      <div class="modal-header">
        <h4 class="modal-title">
          <i class="fas fa-info-circle"></i>
          Detalhes do Log
        </h4>
        <button @click="$emit('close')" class="btn-close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body" v-if="log">
        <div class="log-detail-section">
          <label class="detail-label">ID</label>
          <div class="detail-value log-id">#{{ log.id }}</div>
        </div>

        <div class="log-detail-section">
          <label class="detail-label">Data/Hora</label>
          <div class="detail-value">
            {{ formatDetailTimestamp(log.created_at) }}
          </div>
        </div>

        <div class="log-detail-section">
          <label class="detail-label">Usuário</label>
          <div class="detail-value">
            <div class="user-info">
              <div class="user-name">
                {{ log.user?.full_name || "Sistema" }}
              </div>
              <div class="user-email">
                {{ log.user?.email || "system@rifa365.com" }}
              </div>
            </div>
          </div>
        </div>

        <div class="log-detail-section">
          <label class="detail-label">Nível</label>
          <div class="detail-value">
            <span
              class="level-badge"
              :class="getLogLevelClass(log.description)"
            >
              {{ getLogLevel(log.description) }}
            </span>
          </div>
        </div>

        <div class="log-detail-section">
          <label class="detail-label">Módulo</label>
          <div class="detail-value">
            <span class="module-badge">{{
              log.module || extractModule(log.description)
            }}</span>
          </div>
        </div>

        <div class="log-detail-section">
          <label class="detail-label">Ação</label>
          <div class="detail-value">
            <span class="action-badge">{{
              log.action || extractAction(log.description)
            }}</span>
          </div>
        </div>

        <div class="log-detail-section" v-if="log.severity">
          <label class="detail-label">Severidade</label>
          <div class="detail-value">{{ log.severity }}</div>
        </div>

        <div class="log-detail-section" v-if="log.route_path">
          <label class="detail-label">Rota</label>
          <div class="detail-value">{{ log.route_path }}</div>
        </div>

        <div class="log-detail-section full-width">
          <label class="detail-label">Descrição</label>
          <div
            class="detail-value description-box"
            v-html="formatLogMessage(log.description)"
          ></div>
        </div>

        <div class="log-detail-section full-width" v-if="log.ip">
          <label class="detail-label">Endereço IP</label>
          <div class="detail-value">{{ log.ip }}</div>
        </div>

        <div class="log-detail-section full-width" v-if="log.user_agent">
          <label class="detail-label">User Agent</label>
          <div class="detail-value user-agent">{{ log.user_agent }}</div>
        </div>

        <div class="log-detail-section full-width" v-if="hasAdditionalData">
          <label class="detail-label">Dados Adicionais</label>
          <div class="detail-value">
            <pre class="json-data">{{ formatAdditionalData() }}</pre>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="copyToClipboard" class="btn-action">
          <i class="fas fa-copy"></i>
          Copiar Log
        </button>
        <button @click="$emit('close')" class="btn-secondary">Fechar</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LogDetailsModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    log: {
      type: Object,
      required: true,
    },
  },
  emits: ["close"],
  computed: {
    // ============================================================================
    // PROPRIEDADES COMPUTADAS
    // ============================================================================
    hasAdditionalData() {
      return this.log.metadata || this.log.data || this.log.context;
    },
  },
  methods: {
    // ============================================================================
    // MÉTODOS DE FORMATAÇÃO E UI
    // ============================================================================
    
    /**
     * Formata a data e hora do log para o padrão brasileiro por extenso
     */
    formatDetailTimestamp(dateString) {
      try {
        if (!dateString) return "Data não disponível";
        
        let date;
        if (dateString.includes("T")) {
          date = new Date(dateString);
        } else {
          date = new Date(dateString.replace(" ", "T"));
        }

        return date.toLocaleString("pt-BR", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
        });
      } catch (error) {
        return "Data inválida";
      }
    },

    /**
     * Determina o nível do log com base na descrição
     */
    getLogLevel(description) {
      if (!description) return "INFO";
      
      if (
        description.includes("[CRIAR]") ||
        description.includes("[ADICIONAR]")
      )
        return "CREATE";
      if (
        description.includes("[ATUALIZAR]") ||
        description.includes("[EDITAR]")
      )
        return "UPDATE";
      if (
        description.includes("[EXCLUIR]") ||
        description.includes("[REMOVER]")
      )
        return "DELETE";
      if (description.includes("[LOGIN]") || description.includes("[LOGOUT]"))
        return "AUTH";
      if (description.includes("[ERROR]") || description.includes("erro"))
        return "ERROR";
      if (description.includes("[WARNING]") || description.includes("aviso"))
        return "WARN";
      return "INFO";
    },

    /**
     * Retorna a classe CSS correspondente ao nível do log
     */
    getLogLevelClass(description) {
      const level = this.getLogLevel(description);
      return level.toLowerCase();
    },

    /**
     * Extrai o módulo do log (primeiro conteúdo entre colchetes)
     */
    extractModule(description) {
      if (!description) return "Sistema";
      const moduleMatch = description.match(/^\[([^\]]+)\]/);
      return moduleMatch ? moduleMatch[1] : "Sistema";
    },

    /**
     * Extrai a ação do log (segundo conteúdo entre colchetes)
     */
    extractAction(description) {
      if (!description) return "INFO";
      const actionMatch = description.match(/\[([^\]]+)\]/g);
      if (actionMatch && actionMatch.length > 1) {
        return actionMatch[1].replace(/[[\]]/g, "");
      }
      return "INFO";
    },

    /**
     * Formata a mensagem do log aplicando spans coloridos
     */
    formatLogMessage(description) {
      if (!description) return "";
      
      // Destacar módulos entre colchetes
      let formatted = description.replace(
        /\[([^\]]+)\]/g,
        '<span class="log-module">[$1]</span>'
      );

      // Destacar IDs e números
      formatted = formatted.replace(
        /ID: (\d+)/gi,
        'ID: <span class="log-id">$1</span>'
      );
      formatted = formatted.replace(
        /#(\d+)/g,
        '<span class="log-id">#$1</span>'
      );

      // Destacar sucesso/erro
      formatted = formatted.replace(
        /(sucesso|êxito)/gi,
        '<span class="log-success">$1</span>'
      );
      formatted = formatted.replace(
        /(erro|falha|error)/gi,
        '<span class="log-error">$1</span>'
      );

      return formatted;
    },

    /**
     * Formata dados adicionais (JSON) para exibição
     */
    formatAdditionalData() {
      const data = this.log.metadata || this.log.data || this.log.context;
      if (typeof data === "object") {
        return JSON.stringify(data, null, 2);
      }
      if (typeof data === "string") {
        try {
          return JSON.stringify(JSON.parse(data), null, 2);
        } catch {
          return data;
        }
      }
      return data;
    },

    /**
     * Copia as informações completas do log para a área de transferência
     */
    async copyToClipboard() {
      const logText = `
Log ID: #${this.log.id}
Timestamp: ${this.formatDetailTimestamp(this.log.created_at)}
User: ${this.log.user?.full_name || "Sistema"} (${
        this.log.user?.email || "system@rifa365.com"
      })
Level: ${this.getLogLevel(this.log.description)}
Module: ${this.extractModule(this.log.description)}
Action: ${this.extractAction(this.log.description)}
Description: ${this.log.description}
${this.log.ip ? `IP: ${this.log.ip}` : ""}
${this.log.user_agent ? `User-Agent: ${this.log.user_agent}` : ""}
${this.hasAdditionalData ? `Data: ${this.formatAdditionalData()}` : ""}
      `.trim();

      try {
        await navigator.clipboard.writeText(logText);
        console.log("Log copiado para a área de transferência");
      } catch (error) {
        console.error("Erro ao copiar log:", error);
      }
    },
  },
};
</script>

<style scoped>
/* Dracula Theme Colors */
.modal-overlay {
  --dracula-bg: #282a36;
  --dracula-current-line: #44475a;
  --dracula-selection: #44475a;
  --dracula-foreground: #f8f8f2;
  --dracula-comment: #6272a4;
  --dracula-cyan: #8be9fd;
  --dracula-green: #50fa7b;
  --dracula-orange: #ffb86c;
  --dracula-pink: #ff79c6;
  --dracula-purple: #bd93f9;
  --dracula-red: #ff5555;
  --dracula-yellow: #f1fa8c;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 20px;
}

.log-detail-modal {
  background: var(--dracula-bg);
  color: var(--dracula-foreground);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  border: 1px solid var(--dracula-current-line);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: "Fira Code", "Consolas", "Monaco", monospace;
}

.modal-header {
  background: var(--dracula-current-line);
  padding: 16px 20px;
  border-bottom: 1px solid var(--dracula-selection);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  color: var(--dracula-foreground);
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-title i {
  color: var(--dracula-cyan);
}

.btn-close {
  background: none;
  border: none;
  color: var(--dracula-comment);
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: var(--dracula-red);
  color: var(--dracula-foreground);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.log-detail-section {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
}

.log-detail-section.full-width {
  flex-direction: column;
}

.detail-label {
  font-weight: bold;
  color: var(--dracula-cyan);
  min-width: 120px;
  margin-right: 16px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.detail-value {
  flex: 1;
  font-size: 13px;
  line-height: 1.4;
}

.log-id {
  color: var(--dracula-orange);
  font-weight: bold;
  font-family: monospace;
}

.user-info .user-name {
  font-weight: bold;
  color: var(--dracula-purple);
}

.user-info .user-email {
  color: var(--dracula-comment);
  font-size: 11px;
  margin-top: 2px;
}

.level-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 10px;
  text-transform: uppercase;
}

.level-badge.create {
  background: var(--dracula-green);
  color: var(--dracula-bg);
}
.level-badge.update {
  background: var(--dracula-yellow);
  color: var(--dracula-bg);
}
.level-badge.delete {
  background: var(--dracula-red);
  color: var(--dracula-foreground);
}
.level-badge.auth {
  background: var(--dracula-purple);
  color: var(--dracula-foreground);
}
.level-badge.error {
  background: var(--dracula-red);
  color: var(--dracula-foreground);
}
.level-badge.warn {
  background: var(--dracula-orange);
  color: var(--dracula-bg);
}
.level-badge.info {
  background: var(--dracula-cyan);
  color: var(--dracula-bg);
}

.module-badge,
.action-badge {
  background: var(--dracula-pink);
  color: var(--dracula-bg);
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
}

.action-badge {
  background: var(--dracula-purple);
  color: var(--dracula-foreground);
}

.description-box {
  background: var(--dracula-current-line);
  padding: 12px;
  border-radius: 4px;
  border: 1px solid var(--dracula-selection);
  line-height: 1.6;
}

.description-box :deep(.log-module) {
  color: var(--dracula-pink);
  font-weight: bold;
}

.description-box :deep(.log-id) {
  color: var(--dracula-orange);
  font-weight: bold;
}

.description-box :deep(.log-success) {
  color: var(--dracula-green);
  font-weight: bold;
}

.description-box :deep(.log-error) {
  color: var(--dracula-red);
  font-weight: bold;
}

.user-agent {
  font-size: 11px;
  color: var(--dracula-comment);
  word-break: break-all;
}

.json-data {
  background: var(--dracula-current-line);
  padding: 12px;
  border-radius: 4px;
  border: 1px solid var(--dracula-selection);
  font-family: monospace;
  font-size: 11px;
  white-space: pre-wrap;
  overflow-x: auto;
  color: var(--dracula-green);
  margin: 0;
}

.modal-footer {
  background: var(--dracula-current-line);
  padding: 16px 20px;
  border-top: 1px solid var(--dracula-selection);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-action,
.btn-secondary {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  font-family: inherit;
}

.btn-action {
  background: var(--dracula-green);
  color: var(--dracula-bg);
}

.btn-action:hover {
  background: var(--dracula-cyan);
}

.btn-secondary {
  background: var(--dracula-comment);
  color: var(--dracula-foreground);
}

.btn-secondary:hover {
  background: var(--dracula-purple);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .log-detail-modal {
    max-height: 95vh;
  }

  .log-detail-section {
    flex-direction: column;
  }

  .detail-label {
    min-width: auto;
    margin-right: 0;
    margin-bottom: 4px;
  }

  .modal-footer {
    flex-direction: column;
  }

  .btn-action,
  .btn-secondary {
    justify-content: center;
  }
}
</style>